// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import React, { useMemo, useState } from "react";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ButtonProps } from "@insite/mobius/Button";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { css } from "styled-components";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";

interface OwnProps extends ButtonProps {
    extendedStyles?: ConfigureProductButtonStyles;
}

type Props = OwnProps & HasProductContext;
interface ConfigureProductButtonStyles {
    button?: ButtonPresentationProps;
}

const configureProductButtonStyles: ConfigureProductButtonStyles = {
    button: {
        sizeVariant: "small",
        shape: "rounded",
        css: css`
            width: 100%;
        `,
    },
};

const ConfigureProductButton = ({ productContext, extendedStyles, ...props }: Props) => {
    const [styles] = useState(() => mergeToNew(configureProductButtonStyles, extendedStyles));

    const { product } = productContext;
    const configurationType = product.properties.configurationType || product.configurationType;
    const configurableButtonText = useMemo(() => {
        if (
            product.canConfigure ||
            product.isVariantParent ||
            (!!configurationType &&
                product.configurationType !== ConfigurationType.None &&
                configurationType !== ConfigurationType.Fixed)
        ) {
            return translate("Configure Product");
        }
        return translate("View Product");
    }, [product.canConfigure, product.isVariantParent, configurationType]);

    return (
        <Button
            {...styles.button}
            {...props}
            href={productContext.productInfo.productDetailPath}
            data-test-selector="ConfigureProduct"
        >
            {configurableButtonText}
        </Button>
    );
};

export default withProductContext(ConfigureProductButton);
